import {
  PoItemV2,
  PurchaseOrderModelV2,
} from "../../../model/purchase_order_model";
import { LineTaxDisplay, POTaxContext, POTotals, POLineVariant } from "./types";

const round4 = (n: number) => Math.round(n * 10000) / 10000;

export const buildTaxContext = (po: PurchaseOrderModelV2): POTaxContext => ({
  billToCountry: po?.bill_to?.address?.country || "India",
  billToState: po?.bill_to?.address?.state || "",
  partyCountry: po?.party?.mail_detail?.address?.country || "",
  partyState: po?.party?.mail_detail?.address?.state || "",
  isUnregistered: po?.party?.tax_detail?.reg_type === "Unregistered",
  vendorCurrencyShortName: po?.vendor_currency?.short_name ?? "INR",
  vendorCurrencyToInr: po?.vendor_currency_to_inr ?? 1,
});

const displaySameCountry = (ctx: POTaxContext) =>
  ctx.billToCountry !== "" &&
  ctx.partyCountry !== "" &&
  ctx.billToCountry.toLowerCase() === ctx.partyCountry.toLowerCase();

const displayInterState = (ctx: POTaxContext) =>
  ctx.billToState !== "" &&
  ctx.partyState !== "" &&
  ctx.billToState.toLowerCase() !== ctx.partyState.toLowerCase();

export const computeLineTaxDisplay = (
  item: PoItemV2,
  ctx: POTaxContext,
  variant: POLineVariant,
): LineTaxDisplay => {
  const amount =
    item.unit_no * item.price ? round4(item.unit_no * item.price) : "--";

  if (variant === "jobwork" && item.is_inventory_line) {
    return { cgst: "0", sgst: "0", igst: "0", amount };
  }

  const sameCountry = displaySameCountry(ctx);
  const interState = displayInterState(ctx);
  const gstRate = item?.item?.tax_detail?.gst_rate;
  const taxApplicable = item?.item?.tax_applicable;

  const halfGst =
    !interState && sameCountry && taxApplicable && gstRate
      ? gstRate / 2.0
      : "--";
  const cgst = ctx.isUnregistered ? 0 : halfGst;
  const igst = ctx.isUnregistered
    ? 0
    : interState && sameCountry && taxApplicable && gstRate
      ? gstRate
      : "--";

  return { cgst, sgst: cgst, igst, amount };
};

/**
 * PO-level totals. Byte-faithful port of the create/update screen tax
 * useEffect (subtotal + CGST/SGST/IGST amounts, currency-converted). Approval
 * logic stays on the screen — this returns money only.
 */
export const sumPOTotals = (items: PoItemV2[], ctx: POTaxContext): POTotals => {
  let subtotal = 0;
  let cgst = 0;
  let sgst = 0;
  let igst = 0;

  const sameCountry =
    ctx.billToCountry !== "" &&
    ctx.partyCountry !== "" &&
    ctx.billToCountry.toLowerCase() === ctx.partyCountry.toLowerCase();

  const interState =
    ctx.billToState !== "" &&
    ctx.partyState !== "" &&
    ctx.billToCountry !== "" &&
    ctx.partyCountry !== "" &&
    ctx.billToState.toLowerCase() !== ctx.partyState.toLowerCase();

  if (Array.isArray(items) && items.length > 0) {
    items.forEach((item) => {
      const itemTotal = item.unit_no * item.price;
      subtotal += itemTotal;

      const gstRate = item.item?.tax_detail?.gst_rate || 0;
      const taxAmount = gstRate ? (itemTotal * gstRate) / 100 : 0;

      if (interState && sameCountry) {
        if (ctx.vendorCurrencyShortName !== "INR") {
          igst += taxAmount / ctx.vendorCurrencyToInr;
          cgst = 0;
          sgst = 0;
        } else {
          igst += taxAmount;
          cgst = 0;
          sgst = 0;
        }
      } else if (!interState && sameCountry) {
        const half = taxAmount / 2.0;
        if (ctx.vendorCurrencyShortName !== "INR") {
          sgst += half / ctx.vendorCurrencyToInr;
          cgst += half / ctx.vendorCurrencyToInr;
        } else {
          sgst += half;
          cgst += half;
        }
      } else {
        sgst = 0;
        cgst = 0;
        igst = 0;
      }

      if (ctx.isUnregistered) {
        cgst = 0;
        sgst = 0;
        igst = 0;
      }
    });
  }

  const total = subtotal + (cgst ?? 0) + (sgst ?? 0) + (igst ?? 0);
  return { subtotal, cgst, sgst, igst, total };
};
